import { PluginKey, Plugin } from '@tiptap/pm/state';
import { ContextualMenuSpecs } from './ContextualMenuConfig.js';
import { ContextualMenuState } from './ContextualMenuState.js';
import { DecorationSet } from '@tiptap/pm/view';
import { ContextualMenuMeta } from './ContextualMenuMeta.js';
import { YJSMapping } from '../YJSMapping/YJSMappingPlugin.js';
import { isDefined } from '@fabric-app/utils/guards';
import { clamp } from '@fabric-app/utils/number';
import { captureException } from '@fabric-app/compat/capabilities/sentry';

const getTextSafe = (doc, from, to) => {
  if (from === to) return "";
  try {
    return doc.textBetween(from, to);
  } catch (error) {
    captureException(new Error("Failed to get text between from and to"), {
      originalException: error
    });
    return "";
  }
};
const ContextualMenuPluginKey = new PluginKey("contextualMenu");
const ContextualMenuPlugin = ({ editor: _editor, handleDOMEvents, onMenuActive, onMenuInactive, onMenuUpdated }) => {
  const mappedDOMEvents = Object.fromEntries(Object.entries(handleDOMEvents ?? {}).map(([key, value]) => {
    return [
      key,
      (view, event) => {
        const state = ContextualMenuPluginKey.getState(view.state);
        if (!state || !state.active || !value || state.query === null) return;
        return value(state.query, view, state.active, event);
      }
    ];
  }));
  return new Plugin({
    key: ContextualMenuPluginKey,
    view() {
      return {
        update: (editorView, prevState) => {
          const prev = ContextualMenuPluginKey.getState(prevState);
          const current = ContextualMenuPluginKey.getState(editorView.state);
          const started = !prev.active && current.active || prev.active && current.active && prev.decoId !== current.decoId;
          const stopped = prev.active && !current.active;
          const updated = prev.active && !started && !stopped && prev.query !== current.query;
          setTimeout(() => {
            const element = current.mobile ? editorView.dom : editorView.dom.querySelector(current.active?.customSelector ?? `#${current.decoId}`);
            if (stopped && onMenuInactive) {
              onMenuInactive(editorView);
              return;
            }
            if (!element) return;
            if (started && onMenuActive) {
              editorView.focus();
              onMenuActive(current.query, editorView, element, current.active, current.mobile);
            }
            if (updated && onMenuUpdated) {
              onMenuUpdated(current.query, editorView, element, current.active, current.mobile);
            }
          }, 10);
        }
      };
    },
    state: {
      init() {
        return ContextualMenuState.empty;
      },
      apply(tr, prevState, _oldState, state) {
        const newState = prevState;
        const meta = tr.getMeta(ContextualMenuPluginKey);
        if (ContextualMenuMeta.isContextualMenuMeta(meta)) {
          return newState.applyMeta(tr, meta).applyDecoration(tr);
        }
        if (!newState.active) return newState;
        if (newState.mobile) return newState;
        const { decorations } = newState;
        const yMapping = new YJSMapping(state, tr);
        const nextDecorations = decorations.map(yMapping, tr.doc);
        const hasDecoration = nextDecorations.find().length > 0;
        if (!hasDecoration) return newState.reset();
        let from;
        let to;
        if (newState.emptyBlock && tr.docChanged) {
          const resolved = tr.doc.resolve(newState.range.to);
          from = resolved.start();
          to = from + resolved.node().nodeSize - 1;
        } else if (!newState.emptyBlock) {
          const deco = nextDecorations.find()[0];
          from = deco.from;
          to = deco.to;
        } else {
          from = newState.range.from;
          to = newState.range.to;
        }
        const docContentSize = tr.doc.content.size;
        from = clamp(from, 0, docContentSize);
        to = clamp(to, 0, docContentSize);
        const text = getTextSafe(tr.doc, from, to);
        if (isDefined(newState.active.char) && !text.startsWith(newState.active.char) && (!newState.summoned || !newState.active.optionalCharWhenSummoned) || nextDecorations.find(tr.selection.from, tr.selection.to).length === 0) {
          return newState.reset();
        }
        return newState.update({
          query: !isDefined(newState.active.char) || newState.summoned && !text.startsWith(newState.active.char) ? text : text.slice(newState.active.char.length),
          range: {
            from,
            to
          }
        }).applyDecoration(tr);
      }
    },
    props: {
      decorations(state) {
        const pluginState = ContextualMenuPluginKey.getState(state);
        return pluginState?.decorations ?? DecorationSet.empty;
      },
      handleTextInput(view, _from, _to, text) {
        return detectTextInput(view, text);
      },
      handleDOMEvents: {
        ...mappedDOMEvents,
        compositionend(view, event) {
          if (mappedDOMEvents.compositionend?.(view, event)) {
            return;
          }
          const selection = view.state.selection;
          if ("$cursor" in selection && !!selection.$cursor) {
            detectTextInput(view, "");
          }
        },
        pointerdown(view, event) {
          if (mappedDOMEvents.pointerdown?.(view, event)) {
            return;
          }
          const pluginState = ContextualMenuPluginKey.getState(view.state);
          if (!pluginState?.active || !pluginState.mobile) return;
          ContextualMenuMeta.reset(view);
        }
      }
    }
  });
};
const spaceRegex = /\s/;
function detectTextInput(view, text) {
  if (view.composing) return;
  const pluginState = ContextualMenuPluginKey.getState(view.state);
  if (pluginState?.active) {
    if (pluginState.mobile) {
      ContextualMenuMeta.reset(view);
      return;
    }
    return;
  }
  const selection = view.state.selection;
  if (!("$cursor" in selection) || !selection.empty) return;
  const $cursor = selection.$cursor;
  const cursorPos = $cursor.pos;
  const $from = view.state.doc.resolve(cursorPos);
  if ($from.node().type.name === "codeBlock") {
    return false;
  }
  const textBefore = $from.parent.textBetween(Math.max(0, $from.parentOffset - 10), $from.parentOffset, null, "￼") + text;
  const splitText = textBefore.split(spaceRegex);
  for (const spec of Object.values(ContextualMenuSpecs)) {
    if (!isDefined(spec.char)) continue;
    if (splitText[splitText.length - 1] === spec.char) {
      const tr = view.state.tr.setMeta(ContextualMenuPluginKey, new ContextualMenuMeta(spec, cursorPos - (spec.char.length - 1), cursorPos + 1));
      if (text) tr.insertText(text, cursorPos);
      view.dispatch(tr);
      return true;
    }
  }
  return false;
}

export { ContextualMenuPlugin, ContextualMenuPluginKey };
//# sourceMappingURL=ContextualMenuPlugin.js.map
